import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Palette, Upload } from "lucide-react";
import { api, ApiError, type Group, type Workspace } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { Button } from "../components/ui/button.js";
import { Card, CardContent } from "../components/ui/card.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";

/** Issuer picker → workspaces (REQ-1.1) → credential groups (REQ-1.2). */
export function WorkspacesPage() {
  const { me, refresh } = useAuth();
  const memberships = me?.memberships ?? [];
  const [slug, setSlug] = useState(memberships[0]?.issuerSlug ?? "");

  useEffect(() => {
    if (!slug && memberships[0]) setSlug(memberships[0].issuerSlug);
  }, [memberships, slug]);

  if (memberships.length === 0) return <CreateIssuer onCreated={refresh} />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Workspaces</h1>
        <select
          className="h-9 rounded-[var(--radius)] border border-input bg-card px-3 text-sm"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
        >
          {memberships.map((m) => (
            <option key={m.issuerId} value={m.issuerSlug}>
              {m.issuerName} ({m.role})
            </option>
          ))}
        </select>
      </div>
      {slug && <WorkspaceList slug={slug} />}
    </div>
  );
}

function CreateIssuer({ onCreated }: { onCreated: () => Promise<void> }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await api.createIssuer(name, slug);
      await onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to create issuer");
    }
  }

  return (
    <Card className="mx-auto max-w-md">
      <CardContent className="p-8">
        <h1 className="text-lg font-semibold">Create your first issuer</h1>
        <p className="mb-4 mt-1 text-sm text-muted-foreground">
          An issuer is your institution — it signs and owns credentials.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="iss-name">Name</Label>
            <Input id="iss-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="iss-slug">Slug</Label>
            <Input
              id="iss-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
              placeholder="acme-university"
              required
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full">
            Create issuer
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function WorkspaceList({ slug }: { slug: string }) {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [name, setName] = useState("");
  const [wsSlug, setWsSlug] = useState("");
  const [loading, setLoading] = useState(true);

  async function load() {
    setWorkspaces((await api.listWorkspaces(slug)).workspaces);
    setLoading(false);
  }
  useEffect(() => {
    setLoading(true);
    void load();
  }, [slug]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    await api.createWorkspace(slug, name, wsSlug);
    setName("");
    setWsSlug("");
    await load();
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <form onSubmit={create} className="flex flex-wrap items-end gap-3">
            <div className="flex-1">
              <Label htmlFor="ws-name">New workspace</Label>
              <Input id="ws-name" placeholder="School of Business" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="flex-1">
              <Label htmlFor="ws-slug">Slug</Label>
              <Input id="ws-slug" placeholder="business" value={wsSlug} onChange={(e) => setWsSlug(e.target.value.toLowerCase())} required />
            </div>
            <Button type="submit">
              <Plus size={16} /> Add
            </Button>
          </form>
        </CardContent>
      </Card>
      {loading ? (
        <div className="h-24 animate-pulse rounded-[var(--radius-lg)] bg-secondary" />
      ) : workspaces.length === 0 ? (
        <p className="text-sm text-muted-foreground">No workspaces yet — add one above.</p>
      ) : (
        workspaces.map((ws) => <GroupsPanel key={ws.id} slug={slug} workspace={ws} />)
      )}
    </div>
  );
}

function GroupsPanel({ slug, workspace }: { slug: string; workspace: Workspace }) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [name, setName] = useState("");

  async function load() {
    setGroups((await api.listGroups(slug, workspace.id)).groups);
  }
  useEffect(() => {
    void load();
  }, [slug, workspace.id]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    await api.createGroup(slug, workspace.id, name);
    setName("");
    await load();
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold">{workspace.name}</h2>
          <Button asChild variant="secondary" size="sm">
            <Link to={`/issuers/${slug}/workspaces/${workspace.id}/designer`}>
              <Palette size={16} /> Design template
            </Link>
          </Button>
        </div>
        <table className="w-full text-sm">
          <tbody>
            {groups.map((g) => (
              <tr key={g.id} className="border-t border-border">
                <td className="py-2">{g.name}</td>
                <td className="py-2 text-right">
                  <Link className="font-medium text-[var(--color-link)]" to={`/issuers/${slug}/issuance?groupId=${g.id}`}>
                    <Upload size={14} className="mr-1 inline" /> Issue
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <form onSubmit={create} className="mt-3 flex items-end gap-2">
          <Input placeholder="Data Science — Fall 2026" value={name} onChange={(e) => setName(e.target.value)} required />
          <Button type="submit">
            <Plus size={16} /> Group
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
